import React, { useState, useEffect } from 'react';
import { 
    CheckCircle, AlertTriangle, Calendar, LogOut, Clock, ChevronDown, ChevronRight, FileText, Bed, Building2, X 
} from 'lucide-react';
import Sidebar from '../../components/Sidebar';
import API from '../../services/api';
import '../Dashboard.css';

const toArray = (response, keys = []) => {
  const raw = response?.data;
  if (Array.isArray(raw)) return raw;
  if (raw && typeof raw === 'object') {
    for (const key of keys) {
      if (Array.isArray(raw[key])) return raw[key];
    }
    if (Array.isArray(raw.data)) return raw.data;
  }
  return [];
};

const Checkout = () => {
  const [loading, setLoading] = useState(false);
  const [clientsEnChambre, setClientsEnChambre] = useState([]);
  const [lignesEtendues, setLignesEtendues] = useState({});
  const [prolongationFormLigne, setProlongationFormLigne] = useState({});
  const [showProlongationLigne, setShowProlongationLigne] = useState({});
  
  // États pour le départ anticipé
  const [departAnticipeForm, setDepartAnticipeForm] = useState({});
  const [showDepartAnticipe, setShowDepartAnticipe] = useState({});

  // États pour le check-out avec date figée
  const [checkoutForm, setCheckoutForm] = useState({});
  const [showCheckout, setShowCheckout] = useState({});

  // 🏢 États pour l'affectation directe d'une société en cours de séjour
  const [showAffecterSociete, setShowAffecterSociete] = useState({});
  const [societeAffectationForm, setSocieteAffectationForm] = useState({});
  const [societesList, setSocietesList] = useState([]);

  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  const aujourdHui = new Date().toLocaleDateString('fr-CA');

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);
  };

  const extraireDateISO = (dateStr) => {
    if (!dateStr) return '';
    return String(dateStr).split('T')[0];
  };

  const obtenirLendemain = (dateStr) => {
    const iso = extraireDateISO(dateStr);
    if (!iso) return aujourdHui;

    const d = new Date(`${iso}T00:00:00`);
    d.setDate(d.getDate() + 1);

    return d.toISOString().split('T')[0];
  };

  const fetchActiveClients = async () => {
    try {
      setLoading(true);
      const [resData, societesRes] = await Promise.all([
        API.get('/hebergement/reservations').catch(() => ({ data: [] })),
        API.get('/hebergement/societes').catch(() => ({ data: [] }))
      ]);
       
      const allReservations = toArray(resData, ['reservations', 'data']);
      const clientsActifs = [];
       
      allReservations.forEach(r => {
        if (!r) return;
        const statutRes = (r.statut || '').toUpperCase();
        if (r.chambres && Array.isArray(r.chambres) && r.chambres.length > 0) {
          const chambresActives = r.chambres.filter(ch => {
            const stCh = (ch?.statut || '').toUpperCase();
            return stCh === 'CHECK_IN' || stCh === 'EN_COURS';
          });
          if (chambresActives.length > 0) {
            clientsActifs.push({ ...r, chambres: chambresActives, _nombre_chambres_actives: chambresActives.length });
          }
        } else if (statutRes.includes('CHECK_IN') || statutRes.includes('EN_COURS')) {
          clientsActifs.push(r);
        }
      });
      setClientsEnChambre(clientsActifs);
      setSocietesList(toArray(societesRes, ['societes', 'data']));
    } catch (err) {
      console.error("Erreur chargement:", err);
      showToast("Erreur lors du chargement des séjours", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchActiveClients();
  }, []);

  const handleToggleFormProlongation = (cle, dateDefaut) => {
    setShowProlongationLigne(prev => {
      const willOpen = !prev[cle];
      if (willOpen && dateDefaut) {
        const lendemainDefaut = obtenirLendemain(dateDefaut);
        setProlongationFormLigne(current => ({ 
          ...current, 
          [cle]: lendemainDefaut 
        }));
      }
      return { ...prev, [cle]: willOpen };
    });
    setShowDepartAnticipe(prev => ({ ...prev, [cle]: false }));
    setShowCheckout(prev => ({ ...prev, [cle]: false }));
    setShowAffecterSociete(prev => ({ ...prev, [cle]: false }));
  };

  const handleToggleFormDepartAnticipe = (cle) => {
    setShowDepartAnticipe(prev => {
      const willOpen = !prev[cle];
      if (willOpen) {
        setDepartAnticipeForm(current => ({
          ...current,
          [cle]: aujourdHui
        }));
      }
      return { ...prev, [cle]: willOpen };
    });
    setShowProlongationLigne(prev => ({ ...prev, [cle]: false }));
    setShowCheckout(prev => ({ ...prev, [cle]: false }));
    setShowAffecterSociete(prev => ({ ...prev, [cle]: false }));
  };

  const handleToggleFormCheckout = (cle) => {
    setShowCheckout(prev => {
      const willOpen = !prev[cle];
      if (willOpen) {
        setCheckoutForm(current => ({
          ...current,
          [cle]: aujourdHui
        }));
      }
      return { ...prev, [cle]: willOpen };
    });
    setShowProlongationLigne(prev => ({ ...prev, [cle]: false }));
    setShowDepartAnticipe(prev => ({ ...prev, [cle]: false }));
    setShowAffecterSociete(prev => ({ ...prev, [cle]: false }));
  };

  const handleToggleFormAffecterSociete = (cle, currentSocieteId) => {
    setShowAffecterSociete(prev => {
      const willOpen = !prev[cle];
      if (willOpen) {
        setSocieteAffectationForm(current => ({
          ...current,
          [cle]: currentSocieteId || ''
        }));
      }
      return { ...prev, [cle]: willOpen };
    });
    setShowProlongationLigne(prev => ({ ...prev, [cle]: false }));
    setShowDepartAnticipe(prev => ({ ...prev, [cle]: false }));
    setShowCheckout(prev => ({ ...prev, [cle]: false }));
  };

  const handleValiderAffectationSociete = async (e, reservationId) => {
    e.preventDefault();
    const cle = `res-${reservationId}`;
    const selectedSocieteId = societeAffectationForm[cle] || societeAffectationForm[reservationId];

    if (!selectedSocieteId) {
      showToast("Veuillez sélectionner une société partenaire", "error");
      return;
    }

    try {
      setLoading(true);
      await API.put(`/hebergement/reservations/${reservationId}/societe`, { 
        societe_id: selectedSocieteId 
      });
      showToast("Facture affectée à la société avec succès !");
      setShowAffecterSociete(prev => ({ ...prev, [cle]: false, [reservationId]: false }));
      fetchActiveClients();
    } catch (err) {
      showToast(err.response?.data?.error || "Erreur lors de l'affectation à la société", "error");
    } finally {
      setLoading(false);
    }
  };

  // 🏢 Fonction pour détacher la société via la route DELETE dédiée (sans window.confirm)
  const handleDetacherSociete = async (reservationId) => {
    try {
      setLoading(true);
      await API.delete(`/hebergement/reservations/${reservationId}/societe`);
      showToast("Société détachée avec succès !");
      fetchActiveClients();
    } catch (err) {
      showToast(err.response?.data?.error || "Erreur lors du détachement de la société", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleProlongerLigneSejour = async (e, rawTargetId, dateRef) => {
    e.preventDefault();
    const cle = rawTargetId.toString();
    const cleanedId = cle.replace(/^(res-|groupe-|ligne-)/, '');
    const dateActuelle = extraireDateISO(dateRef);
    const rawFormVal = prolongationFormLigne[cle];
    const nouvelleDate = (rawFormVal && rawFormVal.trim() !== '') ? rawFormVal.trim() : obtenirLendemain(dateActuelle);

    if (!nouvelleDate) { 
      showToast("Veuillez sélectionner une date de départ valide", "error"); 
      return; 
    }

    if (nouvelleDate <= dateActuelle) {
      showToast(`La nouvelle date doit être après le ${dateActuelle}.`, "error");
      return;
    }

    try {
      await API.post(`/hebergement/reservations/${cleanedId}/prolonger`, { nouvelle_date_depart: nouvelleDate });
      showToast("Séjour prolongé avec succès !");
      setShowProlongationLigne(prev => ({ ...prev, [cle]: false }));
      fetchActiveClients();
    } catch (err) {
      showToast(err.response?.data?.error || "Erreur lors de la prolongation", "error");
    }
  };

  const handleDepartAnticipeValider = async (e, rawTargetId, clientId, isLigne = false) => {
    e.preventDefault();
    const cle = rawTargetId.toString();
    const cleanedId = cle.replace(/^(res-|groupe-|ligne-sous-)/, '');
    const dateCible = departAnticipeForm[cle] || aujourdHui;

    try {
      setLoading(true);
      await API.post(`/hebergement/reservations/${cleanedId}/depart-anticipe`, { nouvelle_date_depart: dateCible });
      showToast("Départ anticipé enregistré et base mise à jour !");
      
      if (isLigne) {
        window.location.href = `#/admin/hebergement/checkout-facturation?ligne_id=${cleanedId}&client_id=${clientId || ''}&mode=depart_anticipe&date_depart=${dateCible}`;
      } else {
        window.location.href = `#/admin/hebergement/checkout-facturation?reservation_id=${cleanedId}&mode=depart_anticipe&date_depart=${dateCible}`;
      }
    } catch (err) {
      showToast(err.response?.data?.error || "Erreur lors du départ anticipé", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleCheckoutValider = async (e, rawTargetId, clientId, isLigne = false) => {
    e.preventDefault();
    const cle = rawTargetId.toString();
    const cleanedId = cle.replace(/^(res-|groupe-|ligne-sous-)/, '');
    const dateCible = checkoutForm[cle] || aujourdHui;

    try {
      setLoading(true);
      await API.post(`/hebergement/reservations/${cleanedId}/checkout`, { date_depart: dateCible });
      showToast("Check-out validé et chambres libérées !");
      
      if (isLigne) {
        window.location.href = `#/admin/hebergement/checkout-facturation?ligne_id=${cleanedId}&client_id=${clientId || ''}&date_depart=${dateCible}`;
      } else {
        window.location.href = `#/admin/hebergement/checkout-facturation?reservation_id=${cleanedId}&date_depart=${dateCible}`;
      }
    } catch (err) {
      showToast(err.response?.data?.error || "Erreur lors du check-out", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden', background: '#f8fafc' }}>
      <Sidebar />
      <main style={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
        {toast.show && (
          <div style={{...toastContainerStyle, background: toast.type === 'success' ? '#10b981' : '#ef4444'}}>
            {toast.type === 'success' ? <CheckCircle size={18}/> : <AlertTriangle size={18}/>}
            {toast.message}
          </div>
        )}

        <header style={headerStyle}>
          <div>
            <h1 style={{ margin: 0, fontSize: '18px', fontWeight: '900', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <LogOut size={20} color="#dc2626" /> CHECK-OUT & SÉJOURS ACTIFS
            </h1>
            <div style={{ color: '#64748b', fontSize: '12px' }}>
              Suivi des occupants, prolongations, sociétés et facturation ({clientsEnChambre.length} actif(s))
            </div>
          </div>
        </header>

        <div style={{ flex: 1, overflowY: 'auto', padding: '24px 30px', boxSizing: 'border-box' }}>
          <div style={contentCardStyle}>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontWeight: '800', fontSize: '13px', color: '#334155' }}>
                Clients actuellement installés en chambre ({clientsEnChambre.length})
              </span>
            </div>

            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '12px' }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0', color: '#64748b', textTransform: 'uppercase', fontSize: '10px' }}>
                  <th style={{ padding: '10px', width: '40px', textAlign: 'center' }}></th>
                  <th style={thStyle}>Référence / Type</th>
                  <th style={thStyle}>Réservant / Client</th>
                  <th style={thStyle}>Chambres & Occupants</th>
                  <th style={thStyle}>Société Facturée</th>
                  <th style={thStyle}>Départ Prévu</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Actions & Facturation</th>
                </tr>
              </thead>
              <tbody>
                {clientsEnChambre.length === 0 ? (
                  <tr>
                    <td colSpan={7} style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>Aucun client en chambre actuellement.</td>
                  </tr>
                ) : (
                  clientsEnChambre.map((res, idx) => {
                    if (!res) return null;
                    const typeRes = (res.type_reservation || 'INDIVIDUELLE').toUpperCase();
                    const estGroupe = typeRes === 'GROUPE' || (res.chambres && res.chambres.length > 1);
                    const estEtendu = Boolean(lignesEtendues[res.id]);
                    const nbChambresActives = res._nombre_chambres_actives || (res.chambres ? res.chambres.length : 1);
                     
                    const cleIndiv = `res-${res.id}`;
                    const estFormProlongationOuvert = Boolean(showProlongationLigne[cleIndiv]);
                    const estFormDepartAnticipeOuvert = Boolean(showDepartAnticipe[cleIndiv]);
                    const estFormCheckoutOuvert = Boolean(showCheckout[cleIndiv]);
                    const estFormAffecterSocieteOuvert = Boolean(showAffecterSociete[cleIndiv]);
                    const dateDepartPrevueIso = extraireDateISO(res.date_depart_prevue);

                    const societeLiee = societesList.find(s => s.id === res.societe_id);

                    return (
                      <React.Fragment key={res.id || idx}>
                        <tr style={{ borderBottom: '1px solid #f1f5f9', background: estGroupe ? '#faf5ff' : 'transparent' }}>
                          <td style={{ padding: '10px', textAlign: 'center' }}>
                            {estGroupe && (
                              <button onClick={() => setLignesEtendues(prev => ({ ...prev, [res.id]: !prev[res.id] }))} style={{ background: '#ede9fe', border: 'none', color: '#7c3aed', width: '24px', height: '24px', borderRadius: '4px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                                {estEtendu ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                              </button>
                            )}
                          </td>
                          <td style={tdStyle}>
                            <div style={{ fontWeight: '800', color: '#2563eb' }}>#{res.id}</div>
                            <span style={badgeStyle(estGroupe ? 'GROUPE' : typeRes)}>
                              {estGroupe ? `GROUPE (${nbChambresActives} ch.)` : typeRes}
                            </span>
                          </td>
                          <td style={tdStyle}>
                            <div style={{ fontWeight: '800', color: '#1e293b' }}>{res.client_nom || res.reservant_nom}</div>
                            <div style={{ fontSize: '11px', color: '#64748b' }}>{res.client_telephone || res.reservant_telephone || 'Pas de tél'}</div>
                          </td>
                          <td style={tdStyle}>
                            <span style={{ fontWeight: '800', color: '#7c3aed' }}>
                              {estGroupe ? `${nbChambresActives} chambre(s) active(s)` : `Chambre N° ${res.chambres?.[0]?.chambre_numero || res.chambre_numero || 'N/A'}`}
                            </span>
                          </td>
                          <td style={tdStyle}>
                            {societeLiee ? (
                              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                                <span style={{ background: '#eff6ff', color: '#1d4ed8', padding: '3px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '800', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                  <Building2 size={12} /> {societeLiee.nom || societeLiee.raison_sociale}
                                </span>
                                {/* ❌ Bouton de détachement direct géré via le toast */}
                                <button 
                                  type="button"
                                  onClick={() => handleDetacherSociete(res.id)}
                                  title="Détacher la société"
                                  style={{ background: '#fee2e2', color: '#991b1b', border: 'none', borderRadius: '4px', width: '20px', height: '20px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
                                >
                                  <X size={12} />
                                </button>
                              </div>
                            ) : (
                              <span style={{ color: '#94a3b8', fontStyle: 'italic', fontSize: '11px' }}>Aucune société</span>
                            )}
                          </td>
                          <td style={tdStyle}>{dateDepartPrevueIso || '-'}</td>
                          <td style={{ ...tdStyle, textAlign: 'right', display: 'flex', justifyContent: 'flex-end', gap: '6px', alignItems: 'center' }}>
                            <button onClick={() => handleToggleFormAffecterSociete(cleIndiv, res.societe_id)} style={actionBtnStyle('#2563eb')}>
                              <Building2 size={11} /> {res.societe_id ? 'Modifier Société' : 'Affecter Société'}
                            </button>
                            <button onClick={() => handleToggleFormProlongation(cleIndiv, res.date_depart_prevue)} style={actionBtnStyle('#059669')}>
                              <Calendar size={11} /> Prolonger
                            </button>
                            <button onClick={() => handleToggleFormDepartAnticipe(cleIndiv)} style={actionBtnStyle('#d97706')}>
                              <Clock size={11} /> Départ Anticipé
                            </button>
                            {!estGroupe && (
                              <button onClick={() => { window.location.href = `#/admin/hebergement/checkout-facturation?reservation_id=${res.id}&mode=facture`; }} style={actionBtnStyle('#2563eb')}>
                                <FileText size={11} /> Facture
                              </button>
                            )}
                            {estGroupe && (
                              <button onClick={() => { window.location.href = `#/admin/hebergement/checkout-facturation?reservation_id=${res.id}&mode=facture_groupe`; }} style={actionBtnStyle('#7c3aed')}>
                                <FileText size={11} /> Facture Groupe
                              </button>
                            )}
                            <button onClick={() => handleToggleFormCheckout(cleIndiv)} style={actionBtnStyle('#dc2626')}>
                              <LogOut size={13} /> Check-out
                            </button>
                          </td>
                        </tr>

                        {/* Formulaire affectation société */}
                        {estFormAffecterSocieteOuvert && (
                          <tr>
                            <td colSpan="7" style={{ background: '#eff6ff', padding: '14px', borderBottom: '1px dashed #bfdbfe' }}>
                              <form onSubmit={(e) => handleValiderAffectationSociete(e, res.id)} style={{ display: 'flex', alignItems: 'center', gap: '12px', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                                <span style={{ fontSize: '11px', fontWeight: '900', color: '#1e40af', display: 'flex', alignItems: 'center', gap: '5px' }}>
                                  <Building2 size={14} /> Facturer au nom de la société partenaire :
                                </span>
                                <select 
                                  style={{ ...inputStyle, width: '280px', marginTop: '0', padding: '6px' }}
                                  value={societeAffectationForm[cleIndiv] || ''}
                                  onChange={(e) => setSocieteAffectationForm(prev => ({ ...prev, [cleIndiv]: e.target.value }))}
                                  required
                                >
                                  <option value="">-- Sélectionner la société partenaire --</option>
                                  {societesList.map((soc, sIdx) => (
                                    <option key={soc.id || sIdx} value={soc.id}>
                                      {soc.nom || soc.raison_sociale} {soc.nif ? `(NIF: ${soc.nif})` : ''}
                                    </option>
                                  ))}
                                </select>
                                <button type="submit" style={{ padding: '6px 14px', background: '#2563eb', color: 'white', border: 'none', borderRadius: '4px', fontWeight: '800', fontSize: '11px', cursor: 'pointer' }}>Enregistrer l'affectation</button>
                                <button type="button" onClick={() => setShowAffecterSociete(prev => ({ ...prev, [cleIndiv]: false }))} style={{ padding: '6px 10px', background: '#e2e8f0', border: 'none', borderRadius: '4px', fontWeight: '800', fontSize: '11px', cursor: 'pointer' }}>Fermer</button>
                              </form>
                            </td>
                          </tr>
                        )}

                        {/* Formulaire de prolongation */}
                        {estFormProlongationOuvert && (
                          <tr>
                            <td colSpan="7" style={{ background: '#f8fafc', padding: '12px', borderBottom: '1px dashed #cbd5e1' }}>
                              <form onSubmit={(e) => handleProlongerLigneSejour(e, res.id, res.date_depart_prevue)} style={{ display: 'flex', alignItems: 'center', gap: '10px', justifyContent: 'flex-end' }}>
                                <span style={{ fontSize: '11px', fontWeight: '800', color: '#059669' }}>Nouvelle date de départ :</span>
                                <input 
                                  type="date" 
                                  style={{ ...inputStyle, width: '160px', marginTop: '0', padding: '6px' }} 
                                  min={obtenirLendemain(res.date_depart_prevue)} 
                                  value={prolongationFormLigne[cleIndiv] !== undefined ? prolongationFormLigne[cleIndiv] : obtenirLendemain(res.date_depart_prevue)} 
                                  onChange={(e) => setProlongationFormLigne(prev => ({ ...prev, [cleIndiv]: e.target.value }))} 
                                  required 
                                />
                                <button type="submit" style={{ padding: '6px 12px', background: '#059669', color: 'white', border: 'none', borderRadius: '4px', fontWeight: '800', fontSize: '11px', cursor: 'pointer' }}>Valider</button>
                                <button type="button" onClick={() => setShowProlongationLigne(prev => ({ ...prev, [cleIndiv]: false }))} style={{ padding: '6px 10px', background: '#e2e8f0', border: 'none', borderRadius: '4px', fontWeight: '800', fontSize: '11px', cursor: 'pointer' }}>Fermer</button>
                              </form>
                            </td>
                          </tr>
                        )}

                        {/* Formulaire de départ anticipé */}
                        {estFormDepartAnticipeOuvert && (
                          <tr>
                            <td colSpan="7" style={{ background: '#fffbeb', padding: '12px', borderBottom: '1px dashed #fde68a' }}>
                              <form onSubmit={(e) => handleDepartAnticipeValider(e, res.id, res.client_id, false)} style={{ display: 'flex', alignItems: 'center', gap: '10px', justifyContent: 'flex-end' }}>
                                <span style={{ fontSize: '11px', fontWeight: '900', color: '#b45309' }}>Date de départ anticipé (Figée sur aujourd'hui) :</span>
                                <input 
                                  type="date" 
                                  style={{ ...inputStyle, width: '160px', marginTop: '0', padding: '6px', background: '#f1f5f9', fontWeight: 'bold' }} 
                                  value={aujourdHui}
                                  disabled
                                />
                                <button type="submit" style={{ padding: '6px 12px', background: '#d97706', color: 'white', border: 'none', borderRadius: '4px', fontWeight: '800', fontSize: '11px', cursor: 'pointer' }}>Valider & Facturer</button>
                                <button type="button" onClick={() => setShowDepartAnticipe(prev => ({ ...prev, [cleIndiv]: false }))} style={{ padding: '6px 10px', background: '#e2e8f0', border: 'none', borderRadius: '4px', fontWeight: '800', fontSize: '11px', cursor: 'pointer' }}>Fermer</button>
                              </form>
                            </td>
                          </tr>
                        )}

                        {/* Formulaire de Check-out */}
                        {estFormCheckoutOuvert && (
                          <tr>
                            <td colSpan="7" style={{ background: '#fef2f2', padding: '12px', borderBottom: '1px dashed #fecaca' }}>
                              <form onSubmit={(e) => handleCheckoutValider(e, res.id, res.client_id, false)} style={{ display: 'flex', alignItems: 'center', gap: '10px', justifyContent: 'flex-end' }}>
                                <span style={{ fontSize: '11px', fontWeight: '900', color: '#991b1b' }}>Confirmation Check-out (Date figée sur aujourd'hui) :</span>
                                <input 
                                  type="date" 
                                  style={{ ...inputStyle, width: '160px', marginTop: '0', padding: '6px', background: '#f1f5f9', fontWeight: 'bold' }} 
                                  value={aujourdHui}
                                  disabled
                                />
                                <button type="submit" style={{ padding: '6px 12px', background: '#dc2626', color: 'white', border: 'none', borderRadius: '4px', fontWeight: '800', fontSize: '11px', cursor: 'pointer' }}>Confirmer & Ouvrir Facture</button>
                                <button type="button" onClick={() => setShowCheckout(prev => ({ ...prev, [cleIndiv]: false }))} style={{ padding: '6px 10px', background: '#e2e8f0', border: 'none', borderRadius: '4px', fontWeight: '800', fontSize: '11px', cursor: 'pointer' }}>Fermer</button>
                              </form>
                            </td>
                          </tr>
                        )}

                        {/* Sous-tableau chambres de groupe étendu */}
                        {estEtendu && res.chambres && (
                          <tr style={{ background: '#fcf8ff', borderBottom: '2px solid #e2e8f0' }}>
                            <td colSpan="7" style={{ padding: '12px 20px 16px 50px' }}>
                              <div style={{ background: 'white', borderRadius: '8px', border: '1px solid #e9d5ff', padding: '15px' }}>
                                <div style={{ fontSize: '11px', fontWeight: '900', color: '#6b21a8', marginBottom: '10px', textTransform: 'uppercase' }}>
                                  🏨 Détails chambres du groupe #{res.id}
                                </div>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                                  <thead>
                                    <tr style={{ color: '#64748b', borderBottom: '1px solid #f1f5f9', textAlign: 'left' }}>
                                      <th style={{ padding: '8px' }}>Chambre N°</th>
                                      <th style={{ padding: '8px' }}>Occupant</th>
                                      <th style={{ padding: '8px' }}>Période</th>
                                      <th style={{ padding: '8px' }}>Tarif</th>
                                      <th style={{ padding: '8px', textAlign: 'right' }}>Actions</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {res.chambres.map((ch, chIdx) => {
                                      if (!ch) return null;
                                      const ligneId = ch.id || ch.ligne_id || `ligne-${chIdx}`;
                                      const cleLigneSousGroupe = `sous-${ligneId}`;
                                      const estFormProlongationOuvert = Boolean(showProlongationLigne[cleLigneSousGroupe]);
                                      const estFormDepartAnticipeOuvert = Boolean(showDepartAnticipe[cleLigneSousGroupe]);
                                      const estFormCheckoutSousOuvert = Boolean(showCheckout[cleLigneSousGroupe]);
                                      const dateCheckOutLigneIso = extraireDateISO(ch.date_check_out);

                                      return (
                                        <React.Fragment key={cleLigneSousGroupe}>
                                          <tr style={{ borderBottom: '1px solid #faf5ff' }}>
                                            <td style={{ padding: '8px', fontWeight: '800', color: '#5b21b6' }}>Chambre {ch.chambre_numero || '-'}</td>
                                            <td style={{ padding: '8px', fontWeight: '700', color: '#1e293b' }}>{ch.client_nom || '-'}</td>
                                            <td style={{ padding: '8px', color: '#475569' }}>{extraireDateISO(ch.date_check_in)} au {dateCheckOutLigneIso}</td>
                                            <td style={{ padding: '8px', fontWeight: '700', color: '#059669' }}>{Number(ch.tarif_nuit || 0).toLocaleString()} F</td>
                                            <td style={{ padding: '8px', textAlign: 'right', display: 'flex', justifyContent: 'flex-end', gap: '6px' }}>
                                              <button onClick={() => handleToggleFormProlongation(cleLigneSousGroupe, ch.date_check_out)} style={actionBtnStyle('#059669')}>
                                                <Calendar size={11} /> Prolonger
                                              </button>
                                              <button onClick={() => handleToggleFormDepartAnticipe(cleLigneSousGroupe)} style={actionBtnStyle('#d97706')}>
                                                <Clock size={11} /> Départ Anticipé
                                              </button>
                                              <button onClick={() => handleToggleFormCheckout(cleLigneSousGroupe)} style={actionBtnStyle('#dc2626')}>
                                                <LogOut size={11} /> Check-out
                                              </button>
                                            </td>
                                          </tr>
                                          {estFormProlongationOuvert && (
                                            <tr>
                                              <td colSpan="5" style={{ background: '#f8fafc', padding: '12px', borderBottom: '1px dashed #cbd5e1' }}>
                                                <form onSubmit={(e) => handleProlongerLigneSejour(e, ligneId, ch.date_check_out)} style={{ display: 'flex', alignItems: 'center', gap: '10px', justifyContent: 'flex-end' }}>
                                                  <span style={{ fontSize: '11px', fontWeight: '800', color: '#059669' }}>Nouvelle date départ (Chambre {ch.chambre_numero}) :</span>
                                                  <input 
                                                    type="date" 
                                                    style={{ ...inputStyle, width: '160px', marginTop: '0', padding: '6px' }} 
                                                    min={obtenirLendemain(ch.date_check_out)} 
                                                    value={prolongationFormLigne[cleLigneSousGroupe] !== undefined ? prolongationFormLigne[cleLigneSousGroupe] : obtenirLendemain(ch.date_check_out)} 
                                                    onChange={(e) => setProlongationFormLigne(prev => ({ ...prev, [cleLigneSousGroupe]: e.target.value }))} 
                                                    required 
                                                  />
                                                  <button type="submit" style={{ padding: '6px 12px', background: '#059669', color: 'white', border: 'none', borderRadius: '4px', fontWeight: '800', fontSize: '11px', cursor: 'pointer' }}>Valider</button>
                                                  <button type="button" onClick={() => setShowProlongationLigne(prev => ({ ...prev, [cleLigneSousGroupe]: false }))} style={{ padding: '6px 10px', background: '#e2e8f0', border: 'none', borderRadius: '4px', fontWeight: '800', fontSize: '11px', cursor: 'pointer' }}>Fermer</button>
                                                </form>
                                              </td>
                                            </tr>
                                          )}
                                          {estFormDepartAnticipeOuvert && (
                                            <tr>
                                              <td colSpan="5" style={{ background: '#fffbeb', padding: '12px', borderBottom: '1px dashed #fde68a' }}>
                                                <form onSubmit={(e) => handleDepartAnticipeValider(e, ligneId, ch.client_id || res.client_id, true)} style={{ display: 'flex', alignItems: 'center', gap: '10px', justifyContent: 'flex-end' }}>
                                                  <span style={{ fontSize: '11px', fontWeight: '900', color: '#b45309' }}>Départ anticipé (Chambre {ch.chambre_numero}) :</span>
                                                  <input 
                                                    type="date" 
                                                    style={{ ...inputStyle, width: '160px', marginTop: '0', padding: '6px', background: '#f1f5f9', fontWeight: 'bold' }} 
                                                    value={aujourdHui}
                                                    disabled
                                                  />
                                                  <button type="submit" style={{ padding: '6px 12px', background: '#d97706', color: 'white', border: 'none', borderRadius: '4px', fontWeight: '800', fontSize: '11px', cursor: 'pointer' }}>Valider & Facturer</button>
                                                  <button type="button" onClick={() => setShowDepartAnticipe(prev => ({ ...prev, [cleLigneSousGroupe]: false }))} style={{ padding: '6px 10px', background: '#e2e8f0', border: 'none', borderRadius: '4px', fontWeight: '800', fontSize: '11px', cursor: 'pointer' }}>Fermer</button>
                                                </form>
                                              </td>
                                            </tr>
                                          )}
                                          {estFormCheckoutSousOuvert && (
                                            <tr>
                                              <td colSpan="5" style={{ background: '#fef2f2', padding: '12px', borderBottom: '1px dashed #fecaca' }}>
                                                <form onSubmit={(e) => handleCheckoutValider(e, ligneId, ch.client_id || res.client_id, true)} style={{ display: 'flex', alignItems: 'center', gap: '10px', justifyContent: 'flex-end' }}>
                                                  <span style={{ fontSize: '11px', fontWeight: '900', color: '#991b1b' }}>Confirmation Check-out (Chambre {ch.chambre_numero}) :</span>
                                                  <input 
                                                    type="date" 
                                                    style={{ ...inputStyle, width: '160px', marginTop: '0', padding: '6px', background: '#f1f5f9', fontWeight: 'bold' }} 
                                                    value={aujourdHui}
                                                    disabled
                                                  />
                                                  <button type="submit" style={{ padding: '6px 12px', background: '#dc2626', color: 'white', border: 'none', borderRadius: '4px', fontWeight: '800', fontSize: '11px', cursor: 'pointer' }}>Confirmer & Ouvrir Facture</button>
                                                  <button type="button" onClick={() => setShowCheckout(prev => ({ ...prev, [cleLigneSousGroupe]: false }))} style={{ padding: '6px 10px', background: '#e2e8f0', border: 'none', borderRadius: '4px', fontWeight: '800', fontSize: '11px', cursor: 'pointer' }}>Fermer</button>
                                                </form>
                                              </td>
                                            </tr>
                                          )}
                                        </React.Fragment>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>
      <style>{`@keyframes slideInToast { from { transform: translate(-50%, -20px); opacity: 0; } to { transform: translate(-50%, 0); opacity: 1; } }`}</style>
    </div>
  );
};

const headerStyle = { background: 'white', padding: '15px 30px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 };
const contentCardStyle = { background: 'white', borderRadius: '10px', border: '1px solid #e2e8f0', boxShadow: '0 2px 4px rgba(0,0,0,0.02)', overflow: 'hidden' };
const inputStyle = { width: '100%', padding: '10px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', outline: 'none', fontSize: '12px', marginTop: '4px', background: '#fff', boxSizing: 'border-box' };
const thStyle = { padding: '12px 16px', fontSize: '10px', color: '#64748b', textTransform: 'uppercase', fontWeight: '800', textAlign: 'left' };
const tdStyle = { padding: '12px 16px', fontSize: '12px', color: '#334155' };
const actionBtnStyle = (color) => ({ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '6px 10px', borderRadius: '6px', border: `1px solid ${color}30`, background: `${color}10`, color: color, fontSize: '11px', fontWeight: '800', cursor: 'pointer' });
const badgeStyle = (type) => ({ padding: '3px 8px', borderRadius: '4px', fontSize: '9px', fontWeight: '900', background: type === 'GROUPE' ? '#f3e8ff' : '#e0f2fe', color: type === 'GROUPE' ? '#7c3aed' : '#0369a1' });
const toastContainerStyle = { position: 'fixed', top: '20px', left: '50%', transform: 'translateX(-50%)', zIndex: 9999, color: 'white', padding: '10px 20px', borderRadius: '8px', boxShadow: '0 10px 15px rgba(0,0,0,0.1)', display: 'flex', alignItems: 'center', gap: '8px', fontWeight: '800', fontSize: '12px', animation: 'slideInToast 0.3s ease-out' };

export default Checkout;